import type { ApiError, Paginated } from '@manpower/shared';

/**
 * In dev, Vite proxies /api to the API so the httpOnly auth cookies are
 * same-origin. In production VITE_API_URL points at the deployed API.
 */
const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

export class ApiRequestError extends Error {
  readonly status: number;
  /** Field-level messages keyed by dotted path — bind these straight into forms. */
  readonly details?: Record<string, string[]>;
  readonly requestId?: string;
  /**
   * Machine-readable marker from the envelope's `error` field, for the few
   * responses a caller must branch on rather than merely display — the
   * sign-in screen uses it to tell "tell me your role" apart from a failure.
   */
  readonly code?: string;

  constructor(status: number, body: Partial<ApiError>) {
    super(body.message ?? 'Something went wrong. Please try again.');
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = body.details;
    this.requestId = body.requestId;
    this.code = body.error;
  }

  /** The first message for a field, for inline form errors. */
  fieldError(path: string): string | undefined {
    return this.details?.[path]?.[0];
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Serialised into a query string; undefined and empty values are dropped. */
  params?: Record<string, unknown>;
  signal?: AbortSignal;
}

/**
 * A single in-flight refresh, shared by every 401 that arrives while it runs —
 * otherwise a dashboard firing six queries at once would mint six refresh
 * tokens and, with rotation on, invalidate five of them.
 */
let refreshInFlight: Promise<boolean> | null = null;

const refreshSession = async (): Promise<boolean> => {
  refreshInFlight ??= fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

const buildUrl = (path: string, params?: Record<string, unknown>): string => {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!params) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      // Nest's query parser reads repeated keys as an array.
      value.forEach((v) => search.append(key, String(v)));
    } else if (value instanceof Date) {
      search.set(key, value.toISOString());
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
};

/**
 * The language the API should render master data in.
 *
 * Read from the document rather than imported from the i18n module, which would
 * make this file depend on it and risk a cycle. `applyDocumentLanguage` keeps
 * the attribute in step with the chosen language, so it is always current.
 *
 * Without this the API answers in English however the interface is set, and the
 * job categories a candidate reads stay English while the chrome around them is
 * Tamil - which is exactly what happened.
 */
const currentLanguage = (): string => {
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.getAttribute('lang') || 'en';
};

async function send<T>(path: string, options: RequestOptions, isRetry = false): Promise<T> {
  const response = await fetch(buildUrl(path, options.params), {
    method: options.method ?? 'GET',
    credentials: 'include',
    signal: options.signal,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      'Accept-Language': currentLanguage(),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  // One silent refresh-and-retry. `isRetry` stops a revoked session from
  // looping; the refresh endpoint itself is excluded so it cannot recurse.
  if (response.status === 401 && !isRetry && !path.startsWith('/auth/refresh')) {
    if (await refreshSession()) return send<T>(path, options, true);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok) {
    const body: Partial<ApiError> = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : {};
    throw new ApiRequestError(response.status, body);
  }

  return contentType.includes('application/json')
    ? ((await response.json()) as T)
    : (undefined as T);
}

/**
 * A public asset served by the API, addressed by path.
 *
 * Assets like an employer's logo are not fetched through `api` — the browser
 * requests them itself from an `<img src>`, so what is needed is the URL rather
 * than the body. Built from the same base so it follows VITE_API_URL in
 * production instead of assuming same-origin.
 */
export const assetUrl = (path: string): string =>
  `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) =>
    send<T>(path, { method: 'GET', params, signal }),
  post: <T>(path: string, body?: unknown) => send<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => send<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => send<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => send<T>(path, { method: 'DELETE' }),

  /**
   * Triggers a browser download for endpoints that stream a file (report
   * exports, offer letters). Kept separate because these return bytes, not JSON.
   */
  async download(path: string, params?: Record<string, unknown>, fallbackName = 'download'): Promise<void> {
    const response = await fetch(buildUrl(path, params), { credentials: 'include' });
    if (!response.ok) {
      const body: Partial<ApiError> = await response.json().catch(() => ({}));
      throw new ApiRequestError(response.status, body);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? fallbackName;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};

export type { Paginated };
